import React from 'react';

import Button from '#contentful/Button';
import TextButton from '#contentful/TextButton/TextButton';
import Image from '#contentful/Image';
import Quote from '#contentful/Quote';
import PersonWithQuote from '#contentful/PersonWithQuote';
import type { ButtonDefinition } from '#contentful/Button';
import type { TextButtonDefinition } from '#contentful/TextButton/TextButton';
import type { ImageDefinition } from '#contentful/Image';
import type { QuoteDefinition } from '#contentful/Quote';
import type { PersonWithQuoteDefinition } from '#contentful/PersonWithQuote';

import Video from '#contentful/Video';
import type { VideoDefinition } from '#contentful/Video';
import StatCard from '#contentful/StatCard';
import ImageCollage from '#contentful/ImageCollage';
import type { ImageCollageDefinition } from '#contentful/ImageCollage';
import type { StatCardDefinition } from '#contentful/StatCard';
import type { CodeBlockDefinition } from '#contentful/CodeBlock';
import CodeBlock from '#contentful/CodeBlock/CodeBlock';
import { Box } from '@mui/material';

export type AnyRichTextInlineComponentDefinition = (
  ButtonDefinition
  | TextButtonDefinition
  | ImageDefinition
  | ImageCollageDefinition
  | QuoteDefinition
  | PersonWithQuoteDefinition
  | StatCardDefinition
  | VideoDefinition
  | CodeBlockDefinition
);

export type RichTextInlineComponentProps = {
  content: AnyRichTextInlineComponentDefinition,
};

const RichTextInlineComponent = ({
  content,
}: RichTextInlineComponentProps) => {
  if (!content?.internal?.type) {
    return null;
  }

  switch (content.internal?.type) {
    case 'ContentfulComponentButton':
      return (
        <Button
          key={content.id}
          content={content as ButtonDefinition}
          sx={{ px: { xs: 2, md: 0 } }}
        />
      );
    case 'ContentfulComponentTextButton':
      return (
        <TextButton
          key={content.id}
          content={content as TextButtonDefinition}
          sx={{ px: { xs: 2, md: 0 } }}
        />
      );
    case 'ContentfulComponentImage':
      return (
        <Box my={4}>
          <Image key={content.id} content={content as ImageDefinition} />
        </Box>
      );
    case 'ContentfulComponentImageCollage':
      return <ImageCollage key={content.id} content={content as ImageCollageDefinition} />;
    case 'ContentfulComponentQuote':
      return <Quote key={content.id} content={content as QuoteDefinition} />;
    case 'ContentfulComponentPersonWithQuote':
      return <PersonWithQuote key={content.id} content={content as PersonWithQuoteDefinition} />;
    case 'ContentfulComponentStatisticsCard':
      return <StatCard key={content.id} content={content as StatCardDefinition} />;
    case 'ContentfulComponentVideo':
      return <Video key={content.id} content={content as VideoDefinition} />;
    case 'ContentfulComponentCodeBlock':
      return <CodeBlock key={content.id} {...content as CodeBlockDefinition} />;
    default:
      console.error(`Unexpected component type: ${JSON.stringify(content)}`);
      return null;
  }
};

export default RichTextInlineComponent;
